import React, { memo, useMemo, forwardRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import useMode from "../../hooks/useMode";
import useData from "../../hooks/useData";
import useExperience from "../../hooks/useExperience";
import useModal from "../../hooks/useModal";
import useTimer from "../../hooks/useTimer";

import WidgetMain from "../widgets/WidgetMain";

import genClassName from "../../utils/genClassName";
import isFunction from "../../utils/isFunction";

import {
	HONEST_PUBLISHER_ID,
	HONEST_STAGING_PUBLISHER_ID,
	LUMIN_PUBLISHER_ID,
	VYBES_PUBLISHER_ID,
	MERIDIAN_PUBLISHER_ID,
	YUMI_PUBLISHER_ID,
} from "../../publisherConf";
import { WIDGET_TYPE } from "../../conf";

import "./widget.css";
import inPreview from "../../utils/inPreview";
import LeadGen from "../LeadGen";
import EssentialWidget from "../EssentialWidget";
import EngageWidget from "../EngageWidget";

export const WidgetLoaderWrapper = memo(
	forwardRef(
		(
			{
				duration = 3,
				onLoadComplete,
				className = "",
				children,
				playing,
				setPlaying,
				...props
			},
			ref
		) => {
			useTimer({
				numTicks: duration,
				onComplete: onLoadComplete,
				playing,
				setPlaying,
			});

			return (
				<motion.section
					className={genClassName({
						base: "widget-loader-wrapper",
						additional: className,
					})}
					ref={ref}
					{...props}
				>
					{children}
				</motion.section>
			);
		}
	)
);
export const WidgetMainWrapper = memo(
	forwardRef(
		(
			{
				children,
				style,
				className = "",
				onContentRender,
				renderLoader,
				widgetAnimationProps,
				onAnimationComplete,
				...rest
			},
			ref
		) => {
			const { open: showLoader, handleClose: closeLoader } = useModal(
				!!renderLoader
			);

			const { publisher } = useData();
			const { inPPE, isCollapsed, inModal } = useMode();

			const isHonestWidget = useMemo(
				() =>
					publisher === HONEST_STAGING_PUBLISHER_ID ||
					publisher === HONEST_PUBLISHER_ID,
				[publisher]
			);

			const isFullWidthWidget = useMemo(
				() =>
					publisher === VYBES_PUBLISHER_ID ||
					publisher === YUMI_PUBLISHER_ID ||
					publisher === MERIDIAN_PUBLISHER_ID ||
					publisher === LUMIN_PUBLISHER_ID ||
					isHonestWidget,
				[publisher, isHonestWidget]
			);

			useEffect(() => {
				if (showLoader || !isFunction(onContentRender)) return;
				onContentRender();
			}, [renderLoader, showLoader, onContentRender]);

			return (
				<main
					className={genClassName({
						base: "widget",
						conditionals: {
							"widget-in-preview": inPreview,
							"widget-in-ppe": inPPE,
							"widget-in-modal": inModal,
							"widget-is-collapsed": isCollapsed,
							"widget-full-width": isFullWidthWidget,
							"coop-honest-widget": isHonestWidget,
							[`${className}-loading`]: showLoader && className,
						},
						additional: className,
					})}
					style={style}
					ref={ref}
					{...rest}
				>
					<AnimatePresence on>
						{showLoader ? (
							isFunction(renderLoader) ? (
								renderLoader({ onLoadComplete: closeLoader })
							) : (
								renderLoader
							)
						) : (
							<motion.section
								className={genClassName({
									base: "widget-content",
								})}
								{...widgetAnimationProps}
								onAnimationComplete={onAnimationComplete}
							>
								{children}
							</motion.section>
						)}
					</AnimatePresence>
				</main>
			);
		}
	)
);

const Widget = memo(({ ...props }) => {
	const { activeWidgetType } = useExperience();

	const Component = useMemo(() => {
		let Component = LeadGen;
		switch (activeWidgetType) {
			case WIDGET_TYPE.DISCOFEED:
				Component = WidgetMain;
				break;
			case WIDGET_TYPE.LEAD_GEN:
				Component = LeadGen;
				break;
			case WIDGET_TYPE.ESSENTIAL:
				Component = EssentialWidget;
				break;
			case WIDGET_TYPE.ENGAGE:
				Component = EngageWidget;
				break;
			default:
				Component = LeadGen;
				break;
		}
		return Component;
	}, [activeWidgetType]);

	return <Component {...props} />;
});

export default Widget;
